import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  BadgePercent,
  BarChart3,
  Boxes,
  ClipboardList,
  CreditCard,
  FileBarChart,
  FileCheck2,
  FileSearch,
  Gauge,
  MapPinned,
  PackageCheck,
  PackageSearch,
  ReceiptText,
  Search,
  ShoppingCart,
  TrendingUp,
  Truck,
  Users,
  WalletCards,
  X,
} from "lucide-react";
import { selectUserType } from "../utility/Redux/profile";
import { selectToken } from "../utility/Redux/security";
import { decodeToken } from "../utility/constant";
import { themesColor } from "../utility/functions/common";

const reportMeta = {
  "/sale_summary": {
    category: "Sales",
    description: "Review sales, invoice count, payment breakup, and net sale value.",
    icon: BarChart3,
  },
  "/e_invoice_hub": {
    category: "Sales",
    description: "Track generated IRNs, create e-way bills, and manage eligible cancellations.",
    icon: FileCheck2,
  },
  "/margin_report": {
    category: "Sales",
    description: "Track sale margin, cost value, and profit performance.",
    icon: TrendingUp,
  },
  "/voucher_summary": {
    category: "Sales",
    description: "Inspect voucher-wise sales and billing activity.",
    icon: ReceiptText,
  },
  "/offer_list": {
    category: "Sales",
    description: "Check offer usage, discounts, and promotional performance.",
    icon: BadgePercent,
  },
  "/sale_tax_report": {
    category: "Sales",
    description: "Review tax-ready sale data with GST breakup.",
    icon: Gauge,
  },
  "/customer_ledger": {
    category: "Customers",
    description: "View customer debit, credit, and balance transactions.",
    icon: ReceiptText,
  },
  "/customer_balance_report": {
    category: "Customers",
    description: "Compare outstanding customer balances in one place.",
    icon: Users,
  },
  "/Closing_Value_Report": {
    category: "Purchase",
    description: "Track daily stock movement and closing stock value.",
    icon: Boxes,
  },
  "/purchase_summary": {
    category: "Purchase",
    description: "Review supplier purchases, taxes, discounts, and totals.",
    icon: ClipboardList,
  },
  "/debit_note_report": {
    category: "Purchase",
    description: "Track debit notes raised against purchase transactions.",
    icon: FileBarChart,
  },
  "/credit_note_report": {
    category: "Purchase",
    description: "Track credit notes and purchase return adjustments.",
    icon: FileBarChart,
  },
  "/party_ledger": {
    category: "Purchase",
    description: "View party-wise purchase ledger and balances.",
    icon: WalletCards,
  },
  "/item_status": {
    category: "Purchase",
    description: "Check item purchase, stock, and activity status.",
    icon: PackageCheck,
  },
  "/stock_report": {
    category: "Purchase",
    description: "See current stock quantity and value.",
    icon: PackageSearch,
  },
  "/party_bal_report": {
    category: "Purchase",
    description: "Compare supplier and party outstanding balances.",
    icon: Users,
  },
  "/cheque_summary": {
    category: "Purchase",
    description: "Review cheque-wise payment and receipt summary.",
    icon: CreditCard,
  },
  "/date_wise_summary": {
    category: "Purchase",
    description: "Count cheque activity date-wise for quick reconciliation.",
    icon: CreditCard,
  },
  "/fieldway_dfr": {
    category: "FieldWay",
    description: "Inspect field activity and DFR reporting.",
    icon: MapPinned,
  },
  "/fieldway_location": {
    category: "FieldWay",
    description: "Track FieldWay location activity.",
    icon: MapPinned,
  },
  "/issue_summary": {
    category: "Issue",
    description: "Review issue summary for stores and departments.",
    icon: Truck,
  },
  "/recieve_summary": {
    category: "Issue",
    description: "Review received quantity and value summary.",
    icon: PackageCheck,
  },
  "/dep_wise_issue": {
    category: "Issue",
    description: "Track department-wise issued quantities.",
    icon: Boxes,
  },
  "/dep_wise_recieve": {
    category: "Issue",
    description: "Track department-wise received quantities.",
    icon: Boxes,
  },
  "/pending_payments_of_deliverd": {
    category: "Delivery",
    description: "Find pending payments against delivered orders.",
    icon: Truck,
  },
  "/order_report": {
    category: "Orders",
    description: "Review order status, value, and delivery activity.",
    icon: ClipboardList,
  },
  "/item_wise_report": {
    category: "Orders",
    description: "Inspect item-wise order quantity and value.",
    icon: ShoppingCart,
  },
  "/party_Items_Sale": {
    category: "Item Movement",
    description: "See party-wise item sale performance.",
    icon: ShoppingCart,
  },
  "/item_expiry_reports": {
    category: "Item Movement",
    description: "Find items near expiry or already expired.",
    icon: AlertTriangle,
  },
  "/item_wise_purchase_report": {
    category: "Item Movement",
    description: "Review item-wise purchase quantity and amount.",
    icon: PackageSearch,
  },
  "/item_wise_purchase": {
    category: "Item Movement",
    description: "Inspect detailed item purchase history.",
    icon: FileSearch,
  },
  "/item_wise_issue": {
    category: "Item Movement",
    description: "Track item-wise issue quantity and value.",
    icon: Boxes,
  },
  "/item_wise_sale_report": {
    category: "Item Movement",
    description: "Understand item-wise sold quantity, amount, and tax.",
    icon: BarChart3,
  },
};

const reportGroups = [
  {
    category: "Sales",
    hideForUserTypes: ["STORE"],
    reports: [
      ["/sale_summary", "Sale Summary"],
      ["/e_invoice_hub", "E-Invoice Hub"],
      ["/margin_report", "Margin Report"],
      ["/voucher_summary", "Voucher Summary"],
      ["/offer_list", "Offer Report"],
      ["/sale_tax_report", "Sale Tax Report"],
      ["/customer_ledger", "Customer Ledger"],
      ["/customer_balance_report", "Customer Balance"],
    ],
  },
  {
    category: "Purchase",
    reports: [
      ["/Closing_Value_Report", "Daily Stock Movement"],
      ["/purchase_summary", "Purchase Summary"],
      ["/debit_note_report", "Debit Note Report"],
      ["/credit_note_report", "Credit Note Report"],
      ["/party_ledger", "Party Ledger"],
      ["/item_status", "Item Status"],
      ["/stock_report", "Stock Report"],
      ["/party_bal_report", "Party Wise Balance Report"],
      ["/cheque_summary", "Cheque Summary"],
      ["/date_wise_summary", "Date Wise Cheque Count Summary", "/date_wise_cheque_count_summary"],
    ],
  },
  {
    category: "FieldWay",
    reports: [
      ["/fieldway_dfr", "FieldWay DFR", null, "ADMIN"],
      ["/fieldway_location", "FieldWay Location", null, "ADMIN"],
    ],
  },
  {
    category: "Issue",
    reports: [
      ["/issue_summary", "Issue Summary", null, "STORE MANAGER"],
      ["/recieve_summary", "Recieve Summary", null, "STORE MANAGER"],
      ["/dep_wise_issue", "Department Wise Issue Report", null, "STORE MANAGER"],
      ["/dep_wise_recieve", "Department Wise Received Report", null, "STORE MANAGER"],
    ],
  },
  {
    category: "Delivery",
    hideForUserTypes: ["STORE"],
    reports: [
      ["/pending_payments_of_deliverd", "Pending Payments Of Deliverd", null, "STORE MANAGER"],
    ],
  },
  {
    category: "Orders",
    reports: [
      ["/order_report", "Order Report", null, "STORE MANAGER"],
      ["/item_wise_report", "Item Wise Order Report", "/item_wise_order", "STORE MANAGER"],
    ],
  },
  {
    category: "Item Movement",
    reports: [
      ["/party_Items_Sale", "Party Wise Item Sale"],
      ["/item_expiry_reports", "Item Expiry Report", "/item_expiry_report"],
      ["/item_wise_purchase_report", "Item Wise Purchase Report"],
      ["/item_wise_purchase", "Item Purchase history"],
      ["/item_wise_issue", "Item Wise Issue Report", null, "STORE MANAGER"],
      ["/item_wise_sale_report", "Item Wise Sale Report"],
    ],
  },
];

const defaultUserTypes = "MANAGER,USER,STORE";

const buildReports = (navigation) =>
  reportGroups.flatMap((group) =>
    group.reports.map(([path, name, navigatePath, userType]) => {
      const meta = reportMeta[path] || {};
      return {
        name,
        path,
        navigatePath: navigatePath || path,
        category: group.category,
        description: meta.description || "Open and review this report.",
        icon: meta.icon || FileBarChart,
        userType: userType || defaultUserTypes,
        hideForUserTypes: group.hideForUserTypes || [],
        onclick: () => navigation(navigatePath || path),
      };
    })
  );

const normalizeModulePath = (path) => path.replace(/_/g, "-").replace(/\//g, "");

const Reports = () => {
  const navigation = useNavigate();
  const token = useSelector(selectToken);
  const userType = useSelector(selectUserType);
  const userModule = decodeToken(token)?.user_modules || "default";
  const userModules = userModule.split(",");
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const allReports = useMemo(() => buildReports(navigation), [navigation]);

  const accessibleReports = useMemo(
    () =>
      allReports.filter((report) => {
        if (report.hideForUserTypes.includes(userType)) return false;

        if (userModules.includes("default")) {
          return report.userType.toLowerCase().includes(userType.toLowerCase());
        }

        return userModules.some((module) => module === normalizeModulePath(report.path));
      }),
    [allReports, userModules, userType]
  );

  const categories = useMemo(
    () => ["All", ...new Set(accessibleReports.map((report) => report.category))],
    [accessibleReports]
  );

  const filteredReports = useMemo(() => {
    const searchText = search.trim().toLowerCase();

    return accessibleReports.filter((report) => {
      const matchesCategory =
        activeCategory === "All" || report.category === activeCategory;
      const matchesSearch =
        !searchText ||
        `${report.name} ${report.category} ${report.description}`
          .toLowerCase()
          .includes(searchText);

      return matchesCategory && matchesSearch;
    });
  }, [accessibleReports, activeCategory, search]);

  const resetFilters = () => {
    setSearch("");
    setActiveCategory("All");
  };

  return (
    <main className="h-full overflow-auto bg-slate-50 px-4 py-4 text-slate-900 md:px-6 md:py-5">
      <div className="mx-auto max-w-[1500px]">
        <section
          className="relative overflow-hidden rounded-2xl px-5 py-5 text-white shadow-lg md:px-7 md:py-6"
          style={{
            backgroundColor: "#0f172a",
            backgroundImage:
              "radial-gradient(circle at 88% 10%, rgba(254,174,52,0.42), transparent 34%), linear-gradient(135deg, #020617 0%, #0f172a 55%, #431407 100%)",
          }}
        >
          <div className="absolute -right-16 -top-24 h-56 w-56 rounded-full bg-orange/20 blur-3xl" />
          <div className="absolute -bottom-20 left-1/3 h-36 w-36 rounded-full bg-amber-400/10 blur-3xl" />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-semibold text-orange-100 backdrop-blur">
                <FileBarChart size={14} /> Reports Centre
              </div>
              <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
                Find the right report, faster.
              </h1>
              <p className="mt-2 max-w-xl text-sm leading-6 text-slate-300">
                Search by name or choose a category. Each card explains what the report contains before you open it.
              </p>
            </div>
            <div className="flex gap-3">
              <div className="min-w-[120px] rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur">
                <p className="text-2xl font-bold">{accessibleReports.length}</p>
                <p className="text-xs text-slate-300">Available reports</p>
              </div>
              <div className="min-w-[120px] rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur">
                <p className="text-2xl font-bold">{Math.max(0, categories.length - 1)}</p>
                <p className="text-xs text-slate-300">Categories</p>
              </div>
            </div>
          </div>
        </section>

        <section className="-mx-1 mt-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm md:p-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
            <div className="relative min-w-0 flex-1 xl:max-w-md">
              <Search
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                size={18}
              />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search reports..."
                aria-label="Search reports"
                className="h-11 w-full rounded-xl border border-slate-300 bg-slate-50 pl-11 pr-10 text-sm font-medium outline-none transition placeholder:text-slate-400 focus:border-orange focus:bg-white focus:ring-4 focus:ring-orange/20"
              />
              {search ? (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                >
                  <X size={15} />
                </button>
              ) : null}
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 xl:pb-0" aria-label="Report categories">
              {categories.map((category) => (
                <button
                  type="button"
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-semibold transition ${
                    activeCategory === category
                      ? "bg-orange text-white shadow-sm"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="mb-3 mt-5 flex items-end justify-between gap-4 px-1">
          <div>
            <h2 className="text-lg font-bold text-slate-900">
              {activeCategory === "All" ? "All reports" : activeCategory}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              {filteredReports.length} {filteredReports.length === 1 ? "report" : "reports"} found
            </p>
          </div>
        </div>

        {filteredReports.length ? (
          <div className="grid grid-cols-1 gap-3 pb-8 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {filteredReports.map((report) => {
              const Icon = report.icon || FileBarChart;
              return (
                <button
                  type="button"
                  key={report.path}
                  onClick={report.onclick}
                  className="group flex min-h-[156px] flex-col rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-orange hover:shadow-lg hover:shadow-orange-100/70 focus:outline-none focus:ring-4 focus:ring-orange/20"
                >
                  <div className="flex items-start justify-between gap-4">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange/10 text-orange transition group-hover:bg-orange group-hover:text-white">
                      <Icon size={20} />
                    </span>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                      {report.category}
                    </span>
                  </div>
                  <h3 className="mt-4 text-base font-bold leading-5 text-slate-900 group-hover:text-orange">
                    {report.name}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-5 text-slate-500">
                    {report.description}
                  </p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-orange">
                    Open report
                    <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
              <Search size={22} />
            </span>
            <h3 className="mt-4 font-bold text-slate-800">No reports found</h3>
            <p className="mt-1 text-sm text-slate-500">
              Try another search term or clear the selected category.
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-4 rounded-lg bg-orange px-4 py-2 text-sm font-semibold text-white"
            >
              Show all reports
            </button>
          </div>
        )}
      </div>
    </main>
  );
};

export default Reports;

export function buttonCard(i, report) {
  return (
    <button
      key={i}
      onClick={() => report.onclick()}
      style={{
        textTransform: "none",
        backgroundColor: "transparent",
        padding: "0px",
        borderRadius: "7px",
        overflow: "hidden",
        boxShadow: "3px 3px 8px #BDC2C6, -3px -3px 3px #ffffff",
      }}
    >
      <div
        style={{
          backgroundColor: themesColor().backgroundColor,
        }}
        className="py-4 w-full text-black"
      >
        {report.name}
      </div>
    </button>
  );
}
